
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui'
import request from '@/utils/request'
import Pagination from '@/components/Pagination/index.vue'

interface CampaignFeature {
  featureId: number
  durationDays?: number | null
  fixedEndDate?: string | null
  feature?: {
    id: number
    name: string
    featureKey: string
  }
}

interface Campaign {
  id?: number
  campaignKey: string
  name: string
  description?: string
  triggerType: string
  triggerConfig?: any
  startsAt?: string
  endsAt?: string
  maxUses?: number
  maxUsesPerUser: number
  currentUses: number
  isActive: boolean
  campaignFeatures?: CampaignFeature[]
}

interface Feature {
  id: number
  featureKey: string
  name: string
}

@Component({
  name: 'CampaignManagement',
  components: { Pagination }
})
export default class extends Vue {
  private list: Campaign[] = []
  private listLoading = false
  private total = 0
  private listQuery = {
    page: 1,
    limit: 20,
    triggerType: '',
    isActive: ''
  }

  private availableFeatures: Feature[] = []
  private dialogFormVisible = false
  private dialogStatus = 'create'
  private submitLoading = false

  private formData: any = {
    campaignKey: '',
    name: '',
    description: '',
    triggerType: 'registration',
    promoCode: '',
    deviceType: '',
    startsAt: null,
    endsAt: null,
    maxUses: 0,
    maxUsesPerUser: 1,
    isActive: true,
    features: [{ featureId: null, durationMode: 'days', durationDays: 30, fixedEndDate: null }]
  }

  private rules = {
    campaignKey: [{ required: true, message: 'Campaign key is required', trigger: 'blur' }],
    name: [{ required: true, message: 'Name is required', trigger: 'blur' }],
    triggerType: [{ required: true, message: 'Trigger type is required', trigger: 'change' }]
  }

  // Grants
  private grantsDialogVisible = false
  private grantsLoading = false
  private selectedCampaign: Campaign | null = null
  private grants: any[] = []
  private grantsTotal = 0
  private grantsQuery = { page: 1, limit: 20 }

  // Grant User
  private grantUserDialogVisible = false
  private grantLoading = false
  private grantFormData = { userId: '' }

  created() {
    this.getList()
    this.loadFeatures()
  }

  private async getList() {
    this.listLoading = true
    try {
      const params: any = {
        page: this.listQuery.page,
        limit: this.listQuery.limit
      }
      if (this.listQuery.triggerType) params.triggerType = this.listQuery.triggerType
      if (this.listQuery.isActive) params.isActive = this.listQuery.isActive

      const response = await request.get('/admin/campaigns', { params })
      if (response.data.success) {
        this.list = response.data.data.campaigns
        this.total = response.data.data.total
      }
    } catch (error) {
      this.$message.error('Failed to load campaigns')
      console.error(error)
    } finally {
      this.listLoading = false
    }
  }

  private async loadFeatures() {
    try {
      const response = await request.get('/features')
      if (response.data.success) {
        this.availableFeatures = response.data.data
      }
    } catch (error) {
      console.error('Failed to load features:', error)
    }
  }

  private resetForm() {
    this.formData = {
      campaignKey: '',
      name: '',
      description: '',
      triggerType: 'registration',
      promoCode: '',
      deviceType: '',
      startsAt: null,
      endsAt: null,
      maxUses: 0,
      maxUsesPerUser: 1,
      isActive: true,
      features: [{ featureId: null, durationMode: 'days', durationDays: 30, fixedEndDate: null }]
    }
  }

  private handleCreate() {
    this.resetForm()
    this.dialogStatus = 'create'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as ElForm)?.clearValidate()
    })
  }

  private handleEdit(row: Campaign) {
    this.formData = {
      id: row.id,
      campaignKey: row.campaignKey,
      name: row.name,
      description: row.description || '',
      triggerType: row.triggerType,
      promoCode: row.triggerConfig?.promoCode || '',
      deviceType: row.triggerConfig?.deviceType || '',
      startsAt: row.startsAt ? new Date(row.startsAt) : null,
      endsAt: row.endsAt ? new Date(row.endsAt) : null,
      maxUses: row.maxUses || 0,
      maxUsesPerUser: row.maxUsesPerUser || 1,
      isActive: row.isActive,
      features: row.campaignFeatures && row.campaignFeatures.length > 0
        ? row.campaignFeatures.map(cf => ({
          featureId: cf.featureId,
          durationMode: cf.fixedEndDate ? 'date' : 'days',
          durationDays: cf.durationDays || null,
          fixedEndDate: cf.fixedEndDate ? new Date(cf.fixedEndDate) : null
        }))
        : [{ featureId: null, durationMode: 'days', durationDays: 30, fixedEndDate: null }]
    }
    this.dialogStatus = 'update'
    this.dialogFormVisible = true
  }

  private onTriggerTypeChange() {
    this.formData.promoCode = ''
    this.formData.deviceType = ''
  }

  private addFeature() {
    this.formData.features.push({ featureId: null, durationMode: 'days', durationDays: 30, fixedEndDate: null })
  }

  private removeFeature(index: number) {
    if (this.formData.features.length > 1) {
      this.formData.features.splice(index, 1)
    }
  }

  private onDurationModeChange(feature: any) {
    if (feature.durationMode === 'days') {
      feature.fixedEndDate = null
      if (!feature.durationDays) feature.durationDays = 30
    } else {
      feature.durationDays = null
    }
  }

  private async submitForm() {
    (this.$refs.dataForm as ElForm).validate(async(valid: boolean) => {
      if (!valid) return

      // Validate features
      const validFeatures = this.formData.features
        .filter((f: any) => f.featureId)
        .map((f: any) => ({
          featureId: f.featureId,
          durationDays: f.durationMode === 'days' ? (f.durationDays || null) : null,
          fixedEndDate: f.durationMode === 'date' ? (f.fixedEndDate || null) : null
        }))
      if (validFeatures.length === 0) {
        this.$message.error('At least one feature is required')
        return
      }
      const invalidFeature = validFeatures.find((f: any) => !f.durationDays && !f.fixedEndDate)
      if (invalidFeature) {
        this.$message.error(this.$t('campaigns.featureDurationRequired') as string)
        return
      }
      // Aktif kampanyada geçmiş bitiş tarihi, yeni kullanıcılara süresi dolmuş hak verir
      if (this.formData.isActive) {
        const now = Date.now()
        const pastFeature = validFeatures.find((f: any) => f.fixedEndDate && new Date(f.fixedEndDate).getTime() <= now)
        if (pastFeature) {
          this.$message.error(this.$t('campaigns.pastEndDateNotAllowed') as string)
          return
        }
      }

      this.submitLoading = true
      try {
        const triggerConfig: any = {}
        if (this.formData.triggerType === 'promo_code' && this.formData.promoCode) {
          triggerConfig.promoCode = this.formData.promoCode.toUpperCase()
        }
        if (this.formData.triggerType === 'device_activation' && this.formData.deviceType) {
          triggerConfig.deviceType = this.formData.deviceType
        }

        const data = {
          campaignKey: this.formData.campaignKey,
          name: this.formData.name,
          description: this.formData.description,
          triggerType: this.formData.triggerType,
          triggerConfig: Object.keys(triggerConfig).length > 0 ? triggerConfig : undefined,
          startsAt: this.formData.startsAt,
          endsAt: this.formData.endsAt,
          maxUses: this.formData.maxUses > 0 ? this.formData.maxUses : null,
          maxUsesPerUser: this.formData.maxUsesPerUser,
          isActive: this.formData.isActive,
          features: validFeatures
        }

        if (this.dialogStatus === 'create') {
          await request.post('/admin/campaigns', data)
          this.$message.success('Campaign created successfully')
        } else {
          await request.put(`/admin/campaigns/${this.formData.id}`, data)
          this.$message.success('Campaign updated successfully')
        }

        this.dialogFormVisible = false
        this.getList()
      } catch (error: any) {
        this.$message.error(error.response?.data?.error || 'Operation failed')
        console.error(error)
      } finally {
        this.submitLoading = false
      }
    })
  }

  private async handleStatusChange(row: Campaign) {
    try {
      await request.put(`/admin/campaigns/${row.id}`, {
        isActive: row.isActive
      })
      this.$message.success('Status updated')
    } catch (error) {
      row.isActive = !row.isActive
      this.$message.error('Failed to update status')
    }
  }

  private handleViewGrants(row: Campaign) {
    this.selectedCampaign = row
    this.grantsQuery.page = 1
    this.loadGrants()
    this.grantsDialogVisible = true
  }

  private async loadGrants() {
    if (!this.selectedCampaign) return
    this.grantsLoading = true
    try {
      const response = await request.get(
        `/admin/campaigns/${this.selectedCampaign.id}/grants`,
        { params: this.grantsQuery }
      )
      if (response.data.success) {
        this.grants = response.data.data.grants
        this.grantsTotal = response.data.data.total
      }
    } catch (error) {
      this.$message.error('Failed to load grants')
    } finally {
      this.grantsLoading = false
    }
  }

  private showGrantUserDialog() {
    this.grantFormData = { userId: '' }
    this.grantUserDialogVisible = true
  }

  private async grantToUser() {
    if (!this.grantFormData.userId || !this.selectedCampaign) {
      this.$message.error('User ID is required')
      return
    }

    this.grantLoading = true
    try {
      const response = await request.post(
        `/admin/campaigns/${this.selectedCampaign.id}/grant`,
        { userId: this.grantFormData.userId }
      )
      if (response.data.success) {
        this.$message.success('Campaign granted successfully')
        this.grantUserDialogVisible = false
        this.loadGrants()
        this.getList() // Refresh usage count
      } else {
        this.$message.error(response.data.error || 'Failed to grant campaign')
      }
    } catch (error: any) {
      this.$message.error(error.response?.data?.error || 'Failed to grant campaign')
    } finally {
      this.grantLoading = false
    }
  }

  private getTriggerTagType(triggerType: string): string {
    const types: { [key: string]: string } = {
      registration: 'success',
      referral: 'warning',
      device_activation: 'primary',
      promo_code: 'danger',
      subscription_cancel: 'info'
    }
    return types[triggerType] || ''
  }

  private formatTriggerType(triggerType: string): string {
    const labels: { [key: string]: string } = {
      registration: 'Registration',
      referral: 'Referral',
      device_activation: 'Device',
      promo_code: 'Promo Code',
      subscription_cancel: 'Sub Cancel'
    }
    return labels[triggerType] || triggerType
  }

  private formatDate(date: string | undefined): string {
    if (!date) return '-'
    const d = new Date(date)
    return d.toLocaleDateString('tr-TR') + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  }

  private formatCampaignFeatureDuration(cf: CampaignFeature): string {
    if (cf.durationDays && cf.durationDays > 0) {
      return this.$tc('campaigns.durationDays', cf.durationDays, { n: cf.durationDays }) as string
    }
    if (cf.fixedEndDate) {
      const d = new Date(cf.fixedEndDate)
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = d.getFullYear()
      return this.$t('campaigns.untilDate', { date: `${dd}.${mm}.${yyyy}` }) as string
    }
    return '—'
  }
}
